import React, { useState, useEffect } from 'react';
import {
    Briefcase, Plus, Edit2, Trash2, ChevronRight, Users, MapPin,
    Clock, X, ArrowLeft, CheckCircle, XCircle, Building2, Search, ChevronDown
} from 'lucide-react';
import api from '../services/api';

const PIPELINE_STAGES = [
    'Interview waiting', 'Interview Scheduled', 'Interview attended',
    'Offer Received', 'Visa submitted', 'Visa Granted', 'Migrated'
];

const STAGE_COLORS = {
    'Interview waiting': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
    'Interview Scheduled': { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', dot: 'bg-cyan-500' },
    'Interview attended': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500' },
    'Offer Received': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
    'Visa submitted': { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500' },
    'Visa Granted': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
    'Migrated': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
};

const STATUS_BADGE = {
    open: 'bg-green-100 text-green-700',
    closed: 'bg-red-100 text-red-700',
    draft: 'bg-gray-100 text-gray-600',
};

// ─── Assign Candidates Modal ───────────────────────────────
const AssignCandidatesModal = ({ jobId, onClose, onAssigned, showToast }) => {
    const [search, setSearch] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchCandidates = async () => {
            setLoading(true);
            try {
                // Fetch recruiter's candidates pool
                const res = await api.get('/recruiting/candidates');
                let pool = [];
                if (Array.isArray(res.data)) pool = res.data;
                else if (res.data?.candidates) pool = res.data.candidates;

                // Filter locally by search term
                if (search) {
                    pool = pool.filter(c =>
                        c.name?.toLowerCase().includes(search.toLowerCase()) ||
                        c.email?.toLowerCase().includes(search.toLowerCase())
                    );
                }
                setCandidates(pool);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        const debounce = setTimeout(fetchCandidates, 300);
        return () => clearTimeout(debounce);
    }, [search]);

    const toggleSelect = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleAssign = async () => {
        if (selected.length === 0) return;
        try {
            await api.post(`/recruiting/jobs/${jobId}/candidates`, { candidate_ids: selected });
            showToast(`${selected.length} candidate(s) assigned`, 'success');
            onAssigned();
            onClose();
        } catch (e) {
            showToast('Failed to assign candidates', 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900">Assign Candidates to Job</h3>
                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                        <XCircle className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-4 border-b border-slate-100">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 bg-white focus:ring-2 focus:ring-cyan-500 outline-none placeholder-slate-400"
                            placeholder="Search your talent pool by name or email..." autoFocus />
                    </div>
                    {selected.length > 0 && (
                        <p className="text-xs text-cyan-600 font-medium mt-2">{selected.length} candidate(s) selected</p>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {loading ? (
                        <p className="text-center text-sm text-slate-400 py-6">Searching...</p>
                    ) : candidates.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-sm font-medium text-slate-600">No candidates found</p>
                            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
                                {search ? `No candidates match "${search}".` : 'You have no candidates in your talent pool.'}
                            </p>
                        </div>
                    ) : candidates.map(c => (
                        <div key={c.id} onClick={() => toggleSelect(c.id)}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selected.includes(c.id) ? 'border-cyan-400 bg-cyan-50 ring-1 ring-cyan-200' : 'border-slate-200 hover:border-cyan-200 hover:bg-slate-50'
                                }`}>
                            {c.photo_url ? (
                                <img src={c.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                            ) : (
                                <div className="h-9 w-9 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-sm">{c.name?.charAt(0)}</div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                                <p className="text-xs text-slate-500">{c.qualification || 'N/A'} • {c.german_level || 'N/A'}</p>
                            </div>
                            {selected.includes(c.id) && <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0" />}
                        </div>
                    ))}
                </div>
                <div className="p-4 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                    <button onClick={handleAssign} disabled={selected.length === 0}
                        className="px-5 py-2 text-sm font-semibold text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 shadow-sm">
                        Assign {selected.length > 0 ? `(${selected.length})` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Reject Modal ───────────────────────────────
const RejectModal = ({ candidateName, onConfirm, onClose }) => {
    const [reason, setReason] = useState('');
    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Reject Candidate</h3>
                <p className="text-sm text-slate-500 mb-4">This will move <strong>{candidateName}</strong> to the rejected list.</p>
                <textarea value={reason} onChange={e => setReason(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-400 outline-none resize-none"
                    rows={3} placeholder="Reason for rejection (optional)..." />
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                    <button onClick={() => onConfirm(reason)} className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm">Reject</button>
                </div>
            </div>
        </div>
    );
};

// ─── Feedback Modal ───────────────────────────────
const FeedbackModal = ({ candidateName, onConfirm, onClose }) => {
    const [feedback, setFeedback] = useState('');
    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Add Feedback</h3>
                <p className="text-sm text-slate-500 mb-4">Add your feedback for <strong>{candidateName}</strong>.</p>
                <textarea value={feedback} onChange={e => setFeedback(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none resize-none"
                    rows={4} placeholder="Feedback..." />
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                    <button onClick={() => onConfirm(feedback)} disabled={!feedback.trim()} className="px-5 py-2 text-sm font-semibold text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 shadow-sm disabled:opacity-50">Save Task</button>
                </div>
            </div>
        </div>
    );
};

// ─── Interview Modal ───────────────────────────────
const InterviewModal = ({ candidateName, onConfirm, onClose }) => {
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [link, setLink] = useState('');
    const [notes, setNotes] = useState('');

    const handleSubmit = () => {
        if (!date || !time) return;
        onConfirm({ date, time, link, notes });
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Schedule Interview</h3>
                <p className="text-sm text-slate-500 mb-4">Schedule an interview with <strong>{candidateName}</strong>.</p>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Date *</label>
                            <input type="date" value={date} onChange={e => setDate(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none" required />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Time *</label>
                            <input type="time" value={time} onChange={e => setTime(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none" required />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Meeting Link (Optional)</label>
                        <input type="url" value={link} onChange={e => setLink(e.target.value)} placeholder="e.g. Zoom, Google Meet link"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (Optional)</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any specific instructions..."
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none" rows={2} />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                    <button onClick={handleSubmit} disabled={!date || !time} className="px-5 py-2 text-sm font-semibold text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 shadow-sm disabled:opacity-50">Schedule</button>
                </div>
            </div>
        </div>
    );
};

// ─── Job Creation / Editing Form ──────────────────────────────────────
const JobForm = ({ job, onSave, onCancel }) => {
    const [form, setForm] = useState({
        title: job?.title || '',
        description: job?.description || '',
        location: job?.location || '',
        employment_type: job?.employment_type || 'full-time',
        required_qualification: job?.required_qualification || '',
        required_experience: job?.required_experience || '',
        required_german_level: job?.required_german_level || '',
        salary_range: job?.salary_range || '',
        positions_available: job?.positions_available || 1,
        status: job?.status || 'open',
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setSaving(true);
        try {
            if (job) {
                await api.put(`/recruiting/jobs/${job.id}`, form);
            } else {
                await api.post('/recruiting/jobs', form);
            }
            onSave();
        } catch (err) {
            console.error('Error saving job:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900">{job ? 'Edit Job Posting' : 'Create New Job Posting'}</h3>
                <button onClick={onCancel} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
                    <X className="h-5 w-5" />
                </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-5">
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Job Title *</label>
                        <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                            placeholder="e.g. Registered Nurse — Berlin" required />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label>
                        <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all resize-none"
                            rows={3} placeholder="Describe the job requirements and responsibilities..." />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Location</label>
                        <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                            placeholder="e.g. Berlin, Germany" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Employment Type</label>
                        <select value={form.employment_type} onChange={e => setForm({ ...form, employment_type: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all bg-white">
                            <option value="full-time">Full Time</option>
                            <option value="part-time">Part Time</option>
                            <option value="contract">Contract</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Required Qualification</label>
                        <input type="text" value={form.required_qualification} onChange={e => setForm({ ...form, required_qualification: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                            placeholder="e.g. BSc Nursing" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Required Experience</label>
                        <input type="text" value={form.required_experience} onChange={e => setForm({ ...form, required_experience: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                            placeholder="e.g. 2+ years" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Required German Level</label>
                        <select value={form.required_german_level} onChange={e => setForm({ ...form, required_german_level: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all bg-white">
                            <option value="">Not Specified</option>
                            <option value="A1">A1</option><option value="A2">A2</option>
                            <option value="B1">B1</option><option value="B2">B2</option>
                            <option value="C1">C1</option><option value="C2">C2</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Salary Range</label>
                        <input type="text" value={form.salary_range} onChange={e => setForm({ ...form, salary_range: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                            placeholder="e.g. €35,000 - €45,000" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Positions Available</label>
                        <input type="number" min="1" value={form.positions_available} onChange={e => setForm({ ...form, positions_available: parseInt(e.target.value) || 1 })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Status</label>
                        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all bg-white">
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                            <option value="draft">Draft</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                    <button type="button" onClick={onCancel}
                        className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">
                        Cancel
                    </button>
                    <button type="submit" disabled={saving || !form.title.trim()}
                        className="px-5 py-2.5 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-all disabled:opacity-50 shadow-sm">
                        {saving ? 'Saving...' : (job ? 'Update Job' : 'Create Job')}
                    </button>
                </div>
            </form>
        </div>
    );
};

// ─── Pipeline Stage Badge ──────────────────────────────────────
const StageBadge = ({ stage }) => {
    const colors = STAGE_COLORS[stage] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500' };
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`}></span>
            {stage}
        </span>
    );
};

// ─── Job Detail View (with pipeline) ──────────────────────────────────────
const JobDetailView = ({ job, candidates, rejected, onBack, fetchJobCandidates, showToast }) => {
    const [viewMode, setViewMode] = useState('pipeline'); // pipeline | rejected
    const [showAssign, setShowAssign] = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [feedbackTarget, setFeedbackTarget] = useState(null);
    const [interviewTarget, setInterviewTarget] = useState(null);

    const handleStageChange = async (candidateId, newStage) => {
        try {
            await api.put(`/recruiting/jobs/${job.id}/candidates/${candidateId}/stage`, { stage: newStage });
            showToast(`Candidate moved to "${newStage}"`, 'success');
            fetchJobCandidates(job.id);
        } catch (e) {
            showToast('Failed to update stage', 'error');
        }
    };

    const handleReject = async (reason) => {
        if (!rejectTarget) return;
        try {
            await api.put(`/recruiting/jobs/${job.id}/candidates/${rejectTarget.id}/stage`, {
                stage: 'Rejected', rejection_reason: reason
            });
            showToast('Candidate rejected', 'success');
            setRejectTarget(null);
            fetchJobCandidates(job.id);
        } catch (e) {
            showToast('Failed to reject candidate', 'error');
        }
    };

    const handleAddFeedback = async (feedbackText) => {
        if (!feedbackTarget) return;
        try {
            // Reusing the staff endpoint structure for storing feedback logs for candidates 
            await api.post(`/recruiting/candidates/${feedbackTarget.id}/feedback`, {
                feedback: feedbackText
            });
            showToast('Feedback added successfully', 'success');
            setFeedbackTarget(null);
        } catch (e) {
            console.error('Add feedback error:', e);
            showToast('Failed to add feedback', 'error');
        }
    };

    const handleScheduleInterview = async (interviewData) => {
        if (!interviewTarget) return;
        try {
            await api.post(`/recruiting/candidates/${interviewTarget.id}/interviews`, interviewData);
            // After scheduling, move candidate stage to "Interview scheduled" automatically if applicable
            await api.put(`/recruiting/jobs/${job.id}/candidates/${interviewTarget.id}/stage`, {
                stage: 'Interview scheduled'
            });
            showToast('Interview scheduled successfully', 'success');
            setInterviewTarget(null);
            fetchJobCandidates(job.id);
        } catch (e) {
            console.error('Add interview error:', e);
            showToast('Failed to schedule interview', 'error');
        }
    };

    const handleRemoveFromJob = async (candidateId) => {
        if (!window.confirm('Remove this candidate from the job?')) return;
        try {
            await api.delete(`/recruiting/jobs/${job.id}/candidates/${candidateId}`);
            showToast('Candidate removed', 'success');
            fetchJobCandidates(job.id);
        } catch (e) {
            showToast('Failed to remove candidate', 'error');
        }
    };

    const handleRemoveFromRejected = async (rejectedId) => {
        if (!window.confirm('Remove from rejected list?')) return;
        try {
            await api.delete(`/recruiting/jobs/rejected/${rejectedId}`);
            showToast('Removed from rejected list', 'success');
            fetchJobCandidates(job.id);
        } catch (e) {
            showToast('Failed to remove', 'error');
        }
    };

    // Group candidates by stage
    const stageGroups = {};
    PIPELINE_STAGES.forEach(s => { stageGroups[s] = []; });
    (candidates || []).forEach(c => {
        if (stageGroups[c.stage]) stageGroups[c.stage].push(c);
        else if (stageGroups['Interview waiting']) stageGroups['Interview waiting'].push(c);
    });

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button onClick={onBack} className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors">
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1">
                    <h2 className="text-xl font-bold text-slate-900">{job.title}</h2>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        {job.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
                        {job.employment_type && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{job.employment_type}</span>}
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{candidates?.length || 0} candidates</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_BADGE[job.status] || STATUS_BADGE.open}`}>{job.status}</span>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setViewMode('pipeline')}
                        className={`px-3.5 py-2 text-xs font-semibold rounded-lg transition-all ${viewMode === 'pipeline' ? 'bg-cyan-600 text-white shadow-sm' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}>
                        Pipeline
                    </button>
                    <button onClick={() => setViewMode('rejected')}
                        className={`px-3.5 py-2 text-xs font-semibold rounded-lg transition-all ${viewMode === 'rejected' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}>
                        Rejected ({rejected?.length || 0})
                    </button>
                    <button onClick={() => setShowAssign(true)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-cyan-600 text-white rounded-lg text-xs font-semibold hover:bg-cyan-700 transition-all shadow-sm">
                        <Plus className="h-3.5 w-3.5" /> Assign Candidates
                    </button>
                </div>
            </div>

            {/* Pipeline Stages */}
            {viewMode === 'pipeline' && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-cyan-50/50 to-transparent">
                        <h3 className="font-bold text-slate-900">Candidate Pipeline</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Manage and track candidates through the hiring process.</p>
                    </div>

                    {/* Stage progress bar */}
                    <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap gap-1">
                        {PIPELINE_STAGES.map((stage, idx) => {
                            const count = stageGroups[stage]?.length || 0;
                            const colors = STAGE_COLORS[stage] || {};
                            return (
                                <div key={stage} className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${count > 0 ? `${colors.bg} ${colors.text}` : 'bg-slate-50 text-slate-400'}`}>
                                    <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[9px] font-bold ${count > 0 ? `${colors.dot} text-white` : 'bg-slate-200 text-slate-500'}`}>{count}</span>
                                    {stage}
                                </div>
                            );
                        })}
                    </div>

                    {/* Candidate cards by stage */}
                    <div className="p-6 space-y-4">
                        {PIPELINE_STAGES.filter(s => stageGroups[s].length > 0).map(stage => (
                            <div key={stage}>
                                <div className="flex items-center gap-2 mb-2">
                                    <StageBadge stage={stage} />
                                    <span className="text-xs text-slate-400">({stageGroups[stage].length})</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {stageGroups[stage].map(c => (
                                        <div key={c.id} className="bg-slate-50 rounded-lg border border-slate-200 p-3 hover:shadow-sm transition-shadow">
                                            <div className="flex items-center gap-3">
                                                {c.photo_url ? (
                                                    <img src={c.photo_url} alt="" className="h-9 w-9 rounded-full object-cover border border-slate-200" />
                                                ) : (
                                                    <div className="h-9 w-9 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-sm">{c.name?.charAt(0)}</div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                                                    {c.qualification && <p className="text-[11px] text-slate-500 truncate">{c.qualification}</p>}
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
                                                {c.german_level && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-medium">{c.german_level}</span>}
                                                {c.experience && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[10px] font-medium">{c.experience}</span>}
                                                {c.nationality && <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-[10px] font-medium">{c.nationality}</span>}
                                            </div>

                                            <div className="flex items-center justify-between pt-3 border-t border-slate-200/60 mt-auto">
                                                {/* Stage selector */}
                                                <div className="relative">
                                                    <select value={c.stage} onChange={e => handleStageChange(c.id, e.target.value)}
                                                        className="appearance-none bg-white border border-slate-200 rounded-md px-2 py-1 pr-6 text-[10px] font-medium text-slate-700 focus:ring-1 focus:ring-cyan-400 outline-none cursor-pointer">
                                                        {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                    <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400 pointer-events-none" />
                                                </div>

                                                <div className="flex gap-1 items-center">
                                                    <button onClick={() => setInterviewTarget(c)} className="px-2 py-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 transition-colors">
                                                        Interview
                                                    </button>
                                                    <button onClick={() => setFeedbackTarget(c)} className="px-2 py-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-colors">
                                                        Feedback
                                                    </button>
                                                    <a href={`/recruiting/candidates/profile/${c.id}`} target="_blank" rel="noreferrer"
                                                        className="px-2 py-1 text-[10px] font-semibold text-cyan-600 bg-cyan-50 border border-cyan-200 rounded hover:bg-cyan-100 transition-colors">
                                                        Profile
                                                    </a>
                                                    <button onClick={() => setRejectTarget(c)}
                                                        className="px-2 py-1 text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors">
                                                        Reject
                                                    </button>
                                                    <button onClick={() => handleRemoveFromJob(c.id)}
                                                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Remove from job">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {candidates?.length === 0 && (
                            <div className="text-center py-10 text-slate-400">
                                <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                <p className="font-medium">No candidates assigned yet</p>
                                <p className="text-xs mt-1">Assign candidates to this job to start tracking their progress.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Rejected Section */}
            {viewMode === 'rejected' && (
                <div className="bg-white rounded-xl border border-red-100 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-red-50 bg-red-50/50">
                        <h3 className="font-bold text-red-700 flex items-center gap-2">
                            <XCircle className="h-4 w-4" /> Rejected Candidates ({rejected?.length || 0})
                        </h3>
                    </div>
                    <div className="p-4 space-y-2">
                        {!rejected || rejected.length === 0 ? (
                            <p className="text-center py-8 text-sm text-slate-400">No rejected candidates</p>
                        ) : rejected.map(rc => (
                            <div key={rc.id} className="flex items-center gap-3 p-3 bg-red-50/50 rounded-lg border border-red-100">
                                {rc.photo_url ? (
                                    <img src={rc.photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                                ) : (
                                    <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs">{rc.name?.charAt(0)}</div>
                                )}
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-800">{rc.name}</p>
                                    {rc.previous_stage && <p className="text-[11px] text-slate-500">Was at: {rc.previous_stage}</p>}
                                    {rc.rejection_reason && <p className="text-[11px] text-red-500 mt-0.5">Reason: {rc.rejection_reason}</p>}
                                </div>
                                <div className="text-right flex flex-col items-end gap-2">
                                    <span className="text-[10px] text-slate-400">{new Date(rc.rejected_at).toLocaleDateString()}</span>
                                    <button onClick={() => handleRemoveFromRejected(rc.rejection_id)}
                                        className="px-2 py-1 text-[10px] font-medium text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors">
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modals */}
            {showAssign && (
                <AssignCandidatesModal jobId={job.id} onClose={() => setShowAssign(false)} onAssigned={() => fetchJobCandidates(job.id)} showToast={showToast} />
            )}
            {rejectTarget && (
                <RejectModal candidateName={rejectTarget.name} onConfirm={handleReject} onClose={() => setRejectTarget(null)} />
            )}
            {feedbackTarget && (
                <FeedbackModal candidateName={feedbackTarget.name} onConfirm={handleAddFeedback} onClose={() => setFeedbackTarget(null)} />
            )}
            {interviewTarget && (
                <InterviewModal candidateName={interviewTarget.name} onConfirm={handleScheduleInterview} onClose={() => setInterviewTarget(null)} />
            )}
        </div>
    );
};

// ─── Main Jobs Tab ──────────────────────────────────────
const JobsTab = ({ jobs, selectedJob, setSelectedJob, jobCandidates, jobRejected,
    showJobForm, setShowJobForm, editingJob, setEditingJob, fetchJobs, fetchJobCandidates,
    loading, showToast }) => {

    const handleJobClick = (job) => {
        setSelectedJob(job);
        fetchJobCandidates(job.id);
    };

    const handleDeleteJob = async (jobId, e) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this job posting?')) return;
        try {
            await api.delete(`/recruiting/jobs/${jobId}`);
            showToast('Job deleted', 'success');
            fetchJobs();
        } catch (err) {
            showToast('Failed to delete job', 'error');
        }
    };

    const handleFormSave = () => {
        setShowJobForm(false);
        setEditingJob(null);
        showToast(editingJob ? 'Job updated successfully' : 'Job created successfully', 'success');
        fetchJobs();
    };

    // Detail view
    if (selectedJob) {
        return (
            <div className="max-w-6xl mx-auto">
                <JobDetailView
                    job={selectedJob}
                    candidates={jobCandidates}
                    rejected={jobRejected}
                    onBack={() => { setSelectedJob(null); fetchJobs(); }}
                    fetchJobCandidates={fetchJobCandidates}
                    showToast={showToast}
                />
            </div>
        );
    }

    // Form view
    if (showJobForm) {
        return (
            <div className="max-w-4xl mx-auto">
                <JobForm
                    job={editingJob}
                    onSave={handleFormSave}
                    onCancel={() => { setShowJobForm(false); setEditingJob(null); }}
                />
            </div>
        );
    }

    // List view
    return (
        <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-8 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Job Postings</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Create and manage your job postings. Staff will assign qualified candidates.</p>
                </div>
                <button onClick={() => { setEditingJob(null); setShowJobForm(true); }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-cyan-600 text-white rounded-lg text-sm font-semibold hover:bg-cyan-700 transition-all shadow-sm shadow-cyan-500/20">
                    <Plus className="h-4 w-4" /> New Job
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center"><Briefcase className="h-5 w-5 text-green-600" /></div>
                        <div><p className="text-2xl font-bold text-slate-900">{jobs.filter(j => j.status === 'open').length}</p><p className="text-[11px] text-slate-500">Open Jobs</p></div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-cyan-50 rounded-lg flex items-center justify-center"><Users className="h-5 w-5 text-cyan-600" /></div>
                        <div><p className="text-2xl font-bold text-slate-900">{jobs.reduce((s, j) => s + (j.candidate_count || 0), 0)}</p><p className="text-[11px] text-slate-500">Total Candidates</p></div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-slate-50 rounded-lg flex items-center justify-center"><Building2 className="h-5 w-5 text-slate-600" /></div>
                        <div><p className="text-2xl font-bold text-slate-900">{jobs.length}</p><p className="text-[11px] text-slate-500">Total Postings</p></div>
                    </div>
                </div>
            </div>

            {/* Job Cards */}
            {loading ? (
                <div className="text-center py-16 text-slate-400">Loading jobs...</div>
            ) : jobs.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm text-center py-16">
                    <Briefcase className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-semibold text-slate-600">No job postings yet</p>
                    <p className="text-xs text-slate-400 mt-1">Click "New Job" to create your first job posting.</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {jobs.map(job => (
                        <div key={job.id} onClick={() => handleJobClick(job)}
                            className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md hover:border-cyan-200 transition-all cursor-pointer group">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-base font-bold text-slate-900 group-hover:text-cyan-700 transition-colors truncate">{job.title}</h3>
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase flex-shrink-0 ${STATUS_BADGE[job.status] || ''}`}>{job.status}</span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                                        {job.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
                                        {job.employment_type && <span className="capitalize">{job.employment_type}</span>}
                                        {job.required_german_level && <span>German: {job.required_german_level}</span>}
                                        <span>{job.positions_available} position(s)</span>
                                    </div>
                                    {job.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{job.description}</p>}
                                </div>
                                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                    <div className="text-center mr-2">
                                        <p className="text-lg font-bold text-cyan-600">{job.candidate_count || 0}</p>
                                        <p className="text-[10px] text-slate-400">Candidates</p>
                                    </div>
                                    {job.rejected_count > 0 && (
                                        <div className="text-center mr-2">
                                            <p className="text-lg font-bold text-red-500">{job.rejected_count}</p>
                                            <p className="text-[10px] text-slate-400">Rejected</p>
                                        </div>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); setEditingJob(job); setShowJobForm(true); }}
                                        className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors">
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button onClick={(e) => handleDeleteJob(job.id, e)}
                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-cyan-500 transition-colors" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default JobsTab;
